import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { AlertTriangle, CheckCircle2 } from "lucide-react";
import { LOADING_STEPS_KEYS, AI_PROVIDERS } from "../utils/scoring";

export const AnalyzingOverlay = ({ isVisible, progress, providers, context, t }) => {
  const [stepIdx, setStepIdx] = useState(0);

  // Build the rotating fact list. When we already received the loading_context
  // from the backend (after DVSA fetch + engine_knowledge lookup), we replace
  // the generic copy with personalised storytelling that uses the real numbers
  // — peer count, MOT count, make/model. Falls back to the original generic
  // strings while context is still null.
  const facts = (() => {
    if (!context) return LOADING_STEPS_KEYS.map(k => t(k));
    const out = [];
    const family = context.engine_group || '';
    const peers = parseInt(context.peer_count || 0);
    const mots = parseInt(context.total_tests || 0);
    if (peers >= 5 && family) {
      out.push(t('loadingFactPeers').split('{n}').join(String(peers)).split('{family}').join(family));
    } else if (family) {
      out.push(t('loadingFactPeersFew'));
    }
    if (mots > 0) {
      out.push(t('loadingFactMOT').split('{n}').join(String(mots)));
    } else {
      out.push(t('loadingFactMOTNew'));
    }
    if (context.make && context.model) {
      out.push(t('loadingFactAge')
        .split('{make}').join(context.make)
        .split('{model}').join(context.model)
        .split('{year}').join(context.year ? String(context.year) : ''));
    }
    out.push(t('loadingFactSafety'));
    out.push(t('loadingFactScoring'));
    return out.length ? out : LOADING_STEPS_KEYS.map(k => t(k));
  })();

  useEffect(() => {
    if (!isVisible) { setStepIdx(0); return; }
    const interval = context ? 3200 : 650;
    const iv = setInterval(() => setStepIdx(p => (p + 1) % Math.max(facts.length, 1)), interval);
    return () => clearInterval(iv);
  }, [isVisible, context, facts.length]);

  const psychMsg = progress >= 90 ? t('loadingFinalizing')
    : progress >= 68 ? t('loadingIssuesDetected')
    : null;

  const providerStatus = providers || { openai: 'pending', anthropic: 'pending', gemini: 'pending' };

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          key="analyzing-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.4 }}
          style={{ position: 'fixed', inset: 0, zIndex: 300, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}
          data-testid="analyzing-overlay"
          role="status"
          aria-live="polite"
          aria-busy="true"
          aria-label={t('analyzingTitle')}
        >
          <div style={{ position: 'absolute', inset: 0, backgroundImage: "url('https://customer-assets.emergentagent.com/job_6f1a38d7-25a5-407c-b5a8-fbf036123ddb/artifacts/h8cpnww3_markup_1000050801.png')", backgroundSize: 'cover', backgroundPosition: 'center', filter: 'blur(8px)', transform: 'scale(1.08)' }} />
          <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(135deg, rgba(0,0,0,0.78) 0%, rgba(0,0,0,0.68) 50%, rgba(0,0,0,0.78) 100%)' }} />

          <div style={{ position: 'relative', zIndex: 2, textAlign: 'center', padding: '2rem', maxWidth: '520px', width: '100%' }}>
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15 }}
              style={{ color: "var(--wf-modal-text)", fontSize: 'clamp(1.4rem, 4vw, 1.9rem)', fontWeight: 800, marginBottom: '1rem', letterSpacing: '-0.02em' }}
            >
              {t('analyzingTitle')}
            </motion.h2>

            <div style={{ height: '1.6rem', marginBottom: '1.5rem' }}>
              <AnimatePresence mode="wait">
                <motion.p
                  key={stepIdx}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.18 }}
                  style={{ color: "var(--wf-modal-text-muted)", fontSize: '0.9rem', fontWeight: 500 }}
                >
                  {facts[stepIdx] || ''}
                </motion.p>
              </AnimatePresence>
            </div>

            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                gap: '0.55rem',
                marginBottom: '1.5rem',
              }}
              data-testid="ai-provider-cards"
            >
              {AI_PROVIDERS.map(({ key, role }) => {
                const status = providerStatus[key] || 'pending';
                const isDone = status === 'done';
                const isFailed = status === 'failed';
                const isRunning = status === 'pending' && progress >= 25;
                const accent = isDone ? '#22c55e' : isFailed ? '#ef4444' : isRunning ? '#fbbf24' : 'rgba(255,255,255,0.25)';
                return (
                  <motion.div
                    key={key}
                    initial={{ opacity: 0.45, y: 6 }}
                    animate={{
                      opacity: isDone ? 1 : isRunning ? 0.95 : 0.55,
                      y: 0,
                      boxShadow: isDone ? `0 0 26px ${accent}44` : 'none',
                    }}
                    transition={{ duration: 0.3 }}
                    style={{
                      background: 'rgba(255,255,255,0.045)',
                      border: `1px solid ${accent}${isDone ? '' : '55'}`,
                      borderRadius: '14px',
                      padding: '0.75rem 0.5rem 0.65rem',
                      textAlign: 'center',
                      backdropFilter: 'blur(8px)',
                      position: 'relative',
                    }}
                    data-testid={`ai-card-${key}`}
                  >
                    <div style={{ color: "var(--wf-modal-text-muted)", fontWeight: 700, fontSize: '0.8rem', letterSpacing: '-0.01em', lineHeight: 1.2 }}>
                      {t(role)}
                    </div>
                    <div style={{ marginTop: '0.55rem', display: 'flex', justifyContent: 'center', alignItems: 'center', height: '1.1rem' }}>
                      {isDone ? (
                        <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: 'spring', stiffness: 300, damping: 14 }}>
                          <CheckCircle2 className="w-4 h-4" style={{ color: "var(--verdict-color-low)" }} />
                        </motion.div>
                      ) : isFailed ? (
                        <AlertTriangle className="w-4 h-4" style={{ color: "var(--verdict-color-high)" }} />
                      ) : isRunning ? (
                        <motion.div
                          animate={{ rotate: 360 }}
                          transition={{ duration: 1.2, repeat: Infinity, ease: 'linear' }}
                          style={{ width: '0.9rem', height: '0.9rem', borderRadius: '50%', border: '2px solid rgba(251,191,36,0.25)', borderTopColor: '#fbbf24' }}
                        />
                      ) : (
                        <div style={{ width: '0.4rem', height: '0.4rem', borderRadius: '50%', background: 'rgba(255,255,255,0.25)' }} />
                      )}
                    </div>
                  </motion.div>
                );
              })}
            </div>

            <div style={{ width: '320px', maxWidth: '86vw', margin: '0 auto 1.2rem' }}>
              <div style={{ height: '6px', borderRadius: '3px', background: 'rgba(255,255,255,0.10)', overflow: 'hidden' }}>
                <motion.div
                  style={{ height: '100%', borderRadius: '3px', background: 'linear-gradient(90deg, #22c55e, #4ade80)', boxShadow: '0 0 16px rgba(34,197,94,0.55)' }}
                  animate={{ width: `${progress}%` }}
                  transition={{ duration: 0.4, ease: 'easeOut' }}
                />
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '0.45rem' }}>
                <span style={{ color: "var(--wf-modal-text-muted)", fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.05em' }}>
                  {progress}%
                </span>
                <span style={{ color: "var(--wf-modal-text-muted)", fontSize: '0.65rem', letterSpacing: '0.04em' }}>
                  {progress >= 100 ? t('loadingFinalizing') : progress >= 85 ? t('loadingFinalizing') : progress >= 25 ? (t('aiWorking') || 'AI working…') : (t('aiDispatch') || 'Preparing…')}
                </span>
              </div>
            </div>

            <div style={{ minHeight: '2.2rem' }}>
              <AnimatePresence>
                {psychMsg && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ type: 'spring', stiffness: 200, damping: 15 }}
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: '0.4rem',
                      padding: '0.4rem 1rem', borderRadius: '999px',
                      background: progress >= 90 ? 'rgba(34,197,94,0.12)' : 'rgba(245,158,11,0.12)',
                      border: `1px solid ${progress >= 90 ? 'rgba(34,197,94,0.25)' : 'rgba(245,158,11,0.25)'}`,
                    }}
                    data-testid="loading-psych-message"
                  >
                    <AlertTriangle className="w-3.5 h-3.5" style={{ color: progress >= 90 ? '#4ade80' : '#fbbf24' }} />
                    <span style={{ color: progress >= 90 ? '#4ade80' : '#fbbf24', fontSize: '0.82rem', fontWeight: 600 }}>
                      {psychMsg}
                    </span>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Reassurance microcopy — sets correct expectations so users don't bail at 30s */}
            <p
              style={{
                marginTop: '0.7rem',
                color: "var(--wf-modal-text-muted)",
                fontSize: '0.72rem',
                letterSpacing: '0.01em',
              }}
              data-testid="analyzing-reassurance"
            >
              {t('analyzingReassurance') || 'Analysis usually completes in 30–60 seconds'}
            </p>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
